import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isArray } from 'lodash';
import {
  Fade,
  Flex,
  Icon,
  Input,
  Tag,
  TagLabel,
  Tooltip,
} from '@chakra-ui/react';
import { ReactComponent as SearchIcon } from 'resources/images/search.svg';
import { ReactComponent as CloseIcon } from 'resources/images/close-circle-solid.svg';
import { SystemStyleObject } from '@chakra-ui/styled-system';
import { useWindowSize } from 'app/utils/hooks';
import { ChakraAvatar } from 'app/components/Core';
import { useAppDispatch } from 'app/store';
import {
  searchClearedAction,
  searchStartedAction,
} from 'app/store/Mixpanel/slice';
import { useLocation, useMatches } from 'react-router-dom';
import { findMatchPageTitle } from 'app/utils/helpers';
import queryString from 'query-string';
import { QUERY_SEARCH_KEY } from 'app/components/GlobalSearch/constants';
import { styles } from './styles';
import { SearchTag } from '../types';

interface ExpandingInputProps {
  sx?: SystemStyleObject;
  rootElement: HTMLDivElement;
  onChange: (value: string) => void;
  onClear: () => void;
  tag?: SearchTag;
  search?: string;
  placeholder?: string;
  inputWidth?: number;
}

export const ExpandingInput = ({
  sx,
  rootElement,
  onChange: onChangeProp,
  onClear: onClearProp,
  tag,
  search,
  placeholder,
  inputWidth = 540,
}: ExpandingInputProps) => {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState(search || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const searchStartRef = useRef<boolean>(false);
  const [expand, setExpand] = useState(false);
  const [offset, setOffset] = useState(0);
  const { width } = useWindowSize(50);
  const { search: queryParams, pathname } = useLocation();

  const matches = useMatches();

  useLayoutEffect(() => {
    const { width, x } = rootElement.getBoundingClientRect();
    const percentage = window.innerWidth / 100;
    const rightSideContent = window.innerWidth - width - x;
    const inputHalfWidth = inputWidth / 2;
    const offset =
      50 - rightSideContent / percentage - inputHalfWidth / percentage;
    setOffset(offset);
    setExpand(true);
  }, [inputWidth, rootElement, width]);

  const { backgroundSx, searchIconSx, inputSx, clearIconSx } = styles;

  const isEmpty = useMemo(() => {
    return search?.length === 0 && !tag;
  }, [search?.length, tag]);

  const onClear = () => {
    setValue('');
    if (onClearProp) {
      onClearProp();
    }
    inputRef.current?.focus();
    dispatch(searchClearedAction());
  };

  const onChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setValue(value);
    onChangeProp(value);
    if (searchStartRef.current) {
      const pageTitle = findMatchPageTitle(matches);
      dispatch(searchStartedAction(pageTitle));
      searchStartRef.current = false;
    }
  };

  const onFocus = () => {
    searchStartRef.current = true;
  };

  const onBlur = () => {
    searchStartRef.current = false;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Backspace' &&
      !(e.target as HTMLInputElement)?.value &&
      tag
    ) {
      e.stopPropagation();
      onClear();
    }
  };

  useEffect(() => {
    const { [QUERY_SEARCH_KEY]: querySearch } = queryString.parse(queryParams);
    if (querySearch && !isArray(querySearch) && querySearch !== search) {
      setValue(querySearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, pathname]);

  return (
    <Flex width="full" sx={sx} position="relative">
      <Flex sx={backgroundSx(expand, offset, inputWidth)}>
        <Icon sx={searchIconSx} as={SearchIcon} />
        {tag && (
          <Tag
            key={tag.displayName}
            data-testid="global-search-tag"
            sx={{
              rounded: 2,
              flex: '0 0 auto',
              bg: 'surface1',
              ml: 1,
              px: 1,
              py: 1,
              maxWidth: 200,
              minH: 'auto',
              whiteSpace: 'noWrap',
            }}
          >
            <ChakraAvatar
              colors={tag.colors}
              src={tag.imageSrc}
              sx={{
                color: 'M.50',
                width: '16px',
                height: '16px',
              }}
              mr={1}
              size="xxs"
            />
            <Tooltip
              key={tag.displayName}
              label={tag.displayName}
              isDisabled={tag.displayName.length < 32}
              wordBreak="break-all"
            >
              <TagLabel
                sx={{ display: 'block' }}
                fontWeight={500}
                fontSize="xs"
                title={tag.displayName}
              >
                {tag.displayName}
              </TagLabel>
            </Tooltip>
          </Tag>
        )}
        <Input
          sx={inputSx}
          ref={inputRef}
          variant="unstyled"
          value={search !== '' ? value : ''}
          maxLength={255}
          onKeyDown={onKeyDown}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          data-testid="global-search-input"
          placeholder={isEmpty ? placeholder : ''}
        />
        <Fade in={Boolean(expand && !isEmpty)} unmountOnExit>
          <Icon
            sx={clearIconSx}
            as={CloseIcon}
            onClick={onClear}
            data-testid="clear-global-search"
          />
        </Fade>
      </Flex>
    </Flex>
  );
};
